// Thin fetch wrapper. Everything is same-origin: the API serves this bundle in production
// and the dev server proxies /api, so there is no base URL to configure and no CORS to
// negotiate. Session cookies ride along automatically and are never touched from script --
// they are HttpOnly, which is the point.

// `request` is module-level with no React context, so the language is read off the i18next
// singleton -- the same way `usePlayer.getState()` is used outside React elsewhere.
import i18n from "../i18n";
import { nativeRuntime, type NativeFile } from "../platform/native";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
    /** `X-Captcha` from the response: what the server wants done about the captcha. */
    readonly captcha: "required" | "failed" | null = null,
    readonly retryAfterMs: number | null = null,
    readonly errorCode: string | null = null,
    readonly data: unknown = null,
  ) {
    super(detail);
    this.name = "ApiError";
  }
}

type Params = Record<string, string | number | boolean | null | undefined>;

export function qs(params: Params): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === "") continue;
    search.set(key, String(value));
  }
  const rendered = search.toString();
  return rendered ? `?${rendered}` : "";
}

async function parseError(
  response: Response,
): Promise<{ detail: string; errorCode: string | null; data: unknown }> {
  try {
    const body: unknown = await response.json();
    const record =
      typeof body === "object" && body !== null ? (body as Record<string, unknown>) : {};
    const errorCode = typeof record.error_code === "string" ? record.error_code : null;
    if (typeof record.detail === "string") {
      return { detail: record.detail, errorCode, data: body };
    }
    if (Array.isArray(record.detail)) {
      return {
        detail: record.detail.map((item: { msg?: string }) => item.msg ?? "invalid").join("; "),
        errorCode,
        data: body,
      };
    }
    return {
      detail: `${response.status} ${response.statusText}`,
      errorCode,
      data: body,
    };
  } catch {
    return {
      detail: `${response.status} ${response.statusText}`,
      errorCode: null,
      data: null,
    };
  }
}

function retryAfterMs(response: Response): number | null {
  const value = response.headers.get("Retry-After");
  if (!value) return null;
  const seconds = Number(value);
  const delay = Number.isFinite(seconds) ? seconds * 1000 : Date.parse(value) - Date.now();
  return Number.isFinite(delay) ? Math.max(0, delay) : null;
}

export async function readResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const captcha = response.headers.get("X-Captcha");
    const error = await parseError(response);
    throw new ApiError(
      response.status,
      error.detail,
      captcha === "required" || captcha === "failed" ? captcha : null,
      retryAfterMs(response),
      error.errorCode,
      error.data,
    );
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // FormData writes its own Content-Type, with a generated multipart boundary in it. Naming
  // the type here would send a boundary-less header and the server would parse no parts.
  const json = !!init?.body && !(init.body instanceof FormData);
  const initWithHeaders: RequestInit = {
    ...init,
    headers: {
      Accept: "application/json",
      // The app's language, not the browser's raw preference list: the server's error
      // strings must match the UI they land in. Before the spread so a caller can override.
      "Accept-Language": i18n.resolvedLanguage ?? "en",
      ...(json ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  };
  const runtime = nativeRuntime();
  const response = await (runtime
    ? runtime.request(path, initWithHeaders)
    : fetch(path, initWithHeaders));
  return readResponse<T>(response);
}

export async function uploadFile<T>(path: string, file: File | NativeFile): Promise<T> {
  const runtime = nativeRuntime();
  if (runtime !== null) {
    if (!("id" in file)) throw new Error("Native upload requires a picked file");
    return readResponse<T>(await runtime.upload(path, "file", file));
  }
  if ("id" in file) throw new Error("Browser upload requires a browser file");
  const form = new FormData();
  form.append("file", file);
  return api.postForm<T>(path, form);
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "POST",
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  postForm: <T>(path: string, form: FormData) => request<T>(path, { method: "POST", body: form }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
